import { useEffect, useState, useRef } from 'react';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkFrontmatter from 'remark-frontmatter';
import './GitbookFormatting.css';
import {BrowserRouter, Navigate, Route, Routes, useParams} from 'react-router-dom';

// Base path for assets relative to the markdown file's location
const ASSET_BASE = '/TutorialMarkdown/StreamEditor/Assets/';

function preprocessGitbook(raw) {
    return raw
        .replace(/^---[\s\S]*?---\n?/, '')
        .replace(/{%\s*hint\s+style="info"\s*%}([\s\S]*?){%\s*endhint\s*%}/g, (_, body) =>
            `<div class="hint">${body.trim()}</div>`
        )
        .replace(/{%\s*hint\s+style="warning"\s*%}([\s\S]*?){%\s*endhint\s*%}/g, (_, body) =>
            `<div class="hint hint-warning">${body.trim()}</div>`
        )
        .replace(/{%\s*hint\s+style="danger"\s*%}([\s\S]*?){%\s*endhint\s*%}/g, (_, body) =>
            `<div class="hint hint-danger">${body.trim()}</div>`
        )
        .replace(/\.\.\/Assets\//g, ASSET_BASE)
        .replace(/&#x20;/g, ' ')
        .replace(/\\\n/g, '<br />\n');
}

function extractHeadings(text) {
    const headingRe = /^(#{1,3})\s+(.+)$/gm;
    const headings = [];
    let match;
    while ((match = headingRe.exec(text)) !== null) {
        const level = match[1].length;
        const title = match[2].trim();
        const id = title.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
        headings.push({ level, title, id });
    }
    return headings;
}

// Inject id attributes into heading elements so anchor links work
function HeadingWithId({ level, children, ...props }) {
    const Tag = `h${level}`;
    const text = typeof children === 'string' ? children : (children?.[0] ?? '');
    const id = String(text).toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
    return <Tag id={id} {...props}>{children}</Tag>;
}

const pages = {
    intro: "/TutorialMarkdown/StreamEditor/Markdown/Intro.md",
    preRequisites: "/TutorialMarkdown/StreamEditor/Markdown/Pre-Requisites.md",
    contextMenuFileStash: "/TutorialMarkdown/StreamEditor/Markdown/ContextMenuFileStash.md",
    timelineInspector: "/TutorialMarkdown/StreamEditor/Markdown/TimelineInspector.md",
};

const pagesInOrder = [
    { key: 'intro', label: 'Intro', title: 'Introduction' },
    { key: 'preRequisites', label: 'Pre_Requisites', title: '1. Pre-requisites' },
    { key: 'contextMenuFileStash', label: 'ContextMenu_FileStash', title: '2. Context Menu + File Stash' },
    { key: 'timelineInspector', label: 'Timeline_Inspector', title: '3. Timeline + Inspector' },
];

function ServeContent() {
    const { pageKey } = useParams();
    return (
        <>
            <ServePage pageKey={pageKey} />
            <PageNavigation pageKey={pageKey} pagesInOrder={pagesInOrder} />
        </>
    );
}

export default function StreamEditorTutorial() {
    return (
        <Routes>
            <Route path=":pageKey" element={<ServeContent />} />
            <Route path="" element={<Navigate to="intro" replace />} />
        </Routes>
    );
}

function ServePage({ pageKey }) {
    const [rawContent, setRawContent] = useState('');
    const [activeId, setActiveId] = useState('');
    const contentRef = useRef(null);

    useEffect(() => {
        fetch(pages[pageKey])
            .then(res => res.text())
            .then(text => setRawContent(text));
    }, [pageKey]);

    const processed = preprocessGitbook(rawContent);
    const headings = extractHeadings(processed);

    // Highlight active heading in TOC on scroll
    useEffect(() => {
        if (!contentRef.current) return;
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) setActiveId(entry.target.id);
                });
            },
            {rootMargin: '-10% 0px -80% 0px'}
        );
        const nodes = contentRef.current.querySelectorAll('h1,h2,h3,h4,h5,h6');
        nodes.forEach(n => observer.observe(n));
        return () => observer.disconnect();
    }, [processed]);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({behavior: 'smooth', block: 'start'});
    };

    return (
        <div className="gitbook-layout">
            {headings.length > 0 && (
                <aside className="docs-toc">
                    <p className="docs-toc-label">ON THIS PAGE</p>
                    <ul>
                        {headings.map(({id, title, level}) => (
                            <li key={id}
                                className={`docs-toc-item toc-h${level} ${activeId === id ? 'active' : ''}`}>
                                <button onClick={() => scrollTo(id)}>{title}</button>
                            </li>
                        ))}
                    </ul>
                </aside>
            )}

            <main className="docs-body" ref={contentRef}>
                <Markdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkFrontmatter]}
                    components={{
                        h1: (props) => <HeadingWithId level={1} {...props} />,
                        h2: (props) => <HeadingWithId level={2} {...props} />,
                        h3: (props) => <HeadingWithId level={3} {...props} />,
                        h4: (props) => <HeadingWithId level={4} {...props} />,
                        h5: (props) => <HeadingWithId level={5} {...props} />,
                        h6: (props) => <HeadingWithId level={6} {...props} />,
                        audio: ({src}) => (
                            <div className="audio-player">
                                <audio controls>
                                    <source src={src} type="audio/mpeg"/>
                                    Your browser does not support audio.
                                </audio>
                            </div>
                        ),
                    }}
                >
                    {processed}
                </Markdown>
            </main>
            <div></div>
            <div></div>
            <div></div>
            <PageNavigation pagesInOrder={pagesInOrder} pageKey={pageKey}/>
        </div>
    );
}

function PageNavigation({pagesInOrder, pageKey}) {
    const currentIndex = pagesInOrder.findIndex(p => p.key === pageKey);
    const prevPage = currentIndex > 0 ? pagesInOrder[currentIndex - 1] : null;
    const nextPage = currentIndex < pagesInOrder.length - 1 ? pagesInOrder[currentIndex + 1] : null;


    return (
        <div className="page-navigation">
            {prevPage && (
                <a href={`/tutorial-archive/stream-editor/${prevPage.key}`} className="nav-button prev">
                    <span>←</span>
                    <div>
                        <div className="nav-label">PREVIOUS</div>
                        <div className="nav-title">{prevPage.title}</div>
                    </div>
                </a>
            )}
            {nextPage && (
                <a href={`/tutorial-archive/stream-editor/${nextPage.key}`} className="nav-button next">
                    <span>→</span>
                    <div>
                        <div className="nav-label">NEXT</div>
                        <div className="nav-title">{nextPage.title}</div>
                    </div>
                </a>
            )}
        </div>
    );
}